/* ─────────────────────────────────────────────────────────────────────────
 * VINTAGE — brand primitives (framework-agnostic)
 *
 * The raw palette, type stack, and breakpoint constants shared by every
 * surface that carries the Vintage brand: the application (which feeds them
 * into a Mantine theme + CSS-variable resolver — see
 * `apps/fullstack/src/lib/theme.ts`) and the marketing website (which emits
 * them straight to CSS custom properties — see
 * `apps/website/src/lib/tokens.ts`).
 *
 * WHY THIS FILE EXISTS: two apps rendering the same brand must not each carry
 * their own copy of the hexes. A drifted teal is invisible in review and
 * obvious to a customer who has both tabs open.
 *
 * WHAT BELONGS HERE: values with no framework opinion — colour scales, font
 * stacks, breakpoints. Nothing that imports Mantine, React, or the DOM.
 *
 * WHAT DOES NOT: the *semantic* token layer (`--color-primary`, `--bg-surface`,
 * `--fz-h1`, …). Those map primitives onto roles, and the two apps map them
 * differently — the app needs control heights, sidebar widths, and skeleton
 * sheens the website has no concept of. Each app owns its own mapping; both
 * draw from the primitives here.
 *
 * IMPORT PATH: this module is exposed as its own entry point,
 * `@repo/shared/brand` — a deliberate fourth entry alongside server/client/common.
 * The website must be able to reach the palette WITHOUT pulling the `common`
 * barrel, which eagerly re-exports contracts, the uploader core, and the modeling
 * engine (~800 symbols). Dragging that into a brochure site's bundle for ten hex
 * strings is exactly the weight we chose React-with-SSR to avoid. See the
 * `shared-package-architecture` skill.
 *
 * Every text/fill pair below was WCAG-checked (AA+ in light and dark) when the
 * design system was authored; the contrast ratios in the comments are measured,
 * not aspirational. Re-check before changing any value.
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * A ten-shade colour ramp, ordered lightest (0) → darkest (9).
 *
 * Structurally identical to Mantine's `MantineColorsTuple`, restated here so
 * this module stays free of framework imports. `theme.ts` re-asserts each scale
 * against the real Mantine type, so a shape drift fails that app's typecheck.
 */
export type ColorScale = readonly [
  string, string, string, string, string,
  string, string, string, string, string,
];

/* ── Brand palette — Ink (deep teal / petrol) ──
 * The bedrock trust colour. Shade 7 (#193C47) carries white text at 11.8:1
 * (AAA) and is the primary solid fill; 6 is the accent/link tier on light
 * surfaces; 3 is the link tier on dark. */
export const INK: ColorScale = [
  '#EAF2F4', // 50  — palest wash (brand tint bg)
  '#D2E3E8', // 100
  '#A8C7D0', // 200 — pale (dark-mode accent)
  '#79A4B1', // 300 — accent on dark surfaces / dark-mode links
  '#4C7E8E', // 400 — light accent
  '#2F6172', // 500 — focus ring / borders / dark-mode fill
  '#234E5C', // 600 — brand accent: links, icons, primary hover (9.0:1 on white)
  '#193C47', // 700 — primary solid fill (white text 11.8:1, AAA)
  '#122E37', // 800 — primary active / deep
  '#0C2128', // 900 — midnight teal (sidebar / hero / on-accent text)
];

/* ── Accent palette — Gilt (brass / antique gold) ──
 * Used sparingly, like gold leaf. Bright shades (3–4) are fills/borders and take
 * DARK ink text, never white. Deep shades (6) are the text-on-light tier
 * (5.3:1 on white). */
export const BRASS: ColorScale = [
  '#FBF6E9', // 50  — gold tint bg
  '#F4E8C7', // 100
  '#E9D49B', // 200
  '#D9B968', // 300 — accent hover fill (dark text)
  '#C49A3A', // 400 — core brass: accent fill, gold-leaf (dark text)
  '#A87F29', // 500 — accent active fill
  '#896619', // 600 — accent text/icon on light (5.3:1), heading <em> accent
  '#6A4F14', // 700
  '#4C380F', // 800
  '#2E2208', // 900
];

/* ── Neutrals — Paper (warm document grey) ── */
export const NEUTRAL: ColorScale = [
  '#F7F5F1', // 50  — page bg (warm paper)
  '#EFEDE7', // 100 — raised / tab-bar well
  '#E3E0D8', // 200 — sunken / input fill
  '#D0CCC2', // 300 — strong divider
  '#AEA99D', // 400 — disabled fg / placeholder
  '#726E62', // 500 — tertiary fg (5.1:1 on white)
  '#524E42', // 600 — muted chrome / tint-neutral fg
  '#3A372E', // 700 — body text (11.5:1)
  '#28261E', // 800 — headings ink (15.2:1)
  '#1B1812', // 900 — deepest ink
];

/** Ledger status colours (badge tints, alert variants, trend arrows, …). */
export const STATUS = {
  green400: '#2E9460', // credit / positive
  green500: '#1F7D4F',
  green600: '#196B43', // success-text on light (6.5:1)
  green300: '#5DB485', // dark-mode success
  amber400: '#CE9622', // warning (harmonises with brass)
  amber500: '#A8791B',
  amber600: '#875F14', // warning-text on light (5.7:1)
  amber300: '#E0AE3F', // dark-mode warning
  red400: '#C8463B', // debit / negative (4.8:1 white-on-fill)
  red500: '#B23A30',
  red600: '#9C3128', // error-text on light (7.3:1)
  red300: '#DE7468', // dark-mode error
  blue400: '#2D80C2', // info — steel azure, distinct from ink teal
  blue500: '#1F6BA8',
  blue600: '#195A8C', // info-text on light (7.3:1)
  blue300: '#5BA0D6', // dark-mode info
} as const;

/* ── Full status ramps — the ledger palette expressed as ten-shade scales, so
 * component libraries keyed on named colours ("green", "red") render the
 * design's exact hues rather than their own defaults. ── */

export const GREEN_SCALE: ColorScale = [
  '#E9F5EE', '#C9E7D5', '#98D1B0', '#5DB485', '#2E9460',
  '#1F7D4F', '#196B43', '#135233', '#0E3D26', '#082818',
];

export const YELLOW_SCALE: ColorScale = [
  '#FBF2DD', '#F6E1B0', '#EDC974', '#E0AE3F', '#CE9622',
  '#A8791B', '#875F14', '#65470F', '#46310A', '#2A1D06',
];

export const RED_SCALE: ColorScale = [
  '#FBEAE8', '#F6CFC9', '#ECA79E', '#DE7468', '#C8463B',
  '#B23A30', '#9C3128', '#7A2620', '#581B17', '#36100D',
];

export const BLUE_SCALE: ColorScale = [
  '#E7F1FA', '#C6DEF3', '#94C2E7', '#5BA0D6', '#2D80C2',
  '#1F6BA8', '#195A8C', '#144568', '#0E314A', '#081E2E',
];

/** Pure white — the design references it explicitly rather than via a ramp. */
export const WHITE = '#FFFFFF';

/* ── Type stack ──
 * The @font-face declarations are shared too — `packages/shared/styles/fonts.css`,
 * imported by both apps as `@repo/shared/fonts.css`. Only the .woff2 binaries are
 * per-app (Workers serve assets per-worker), guarded by
 * `scripts/check-brand-fonts.mjs`. The STACKS below must name exactly the
 * families declared there.
 *
 *   Montserrat         — body / UI. Geometric sans: clean, contemporary.
 *   Libre Baskerville  — display + headings. The "engraved certificate" voice.
 *   Cormorant Garamond — decorative accent, a single word at a time (usually
 *                        italic + gilt) — the heading <em> flourish.
 *   IBM Plex Mono      — ledger-precise figures: stats, table numerals, code.
 *
 * Each family names a metric-compatible `… Fallback` face ahead of the generic
 * fallback so a pre-webfont paint matches the real face's metrics — no shift.
 */
export const FONT_BODY =
  "'Montserrat', 'Montserrat Fallback', system-ui, -apple-system, sans-serif";
export const FONT_DISPLAY =
  "'Libre Baskerville', 'Libre Baskerville Fallback', Georgia, 'Times New Roman', serif";
export const FONT_ACCENT =
  "'Cormorant Garamond', 'Cormorant Garamond Fallback', Georgia, 'Times New Roman', serif";
export const FONT_MONO =
  "'IBM Plex Mono', 'IBM Plex Mono Fallback', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

/**
 * Optical-size compensation for the accent face.
 *
 * Cormorant Garamond has a smaller x-/cap-height than Libre Baskerville and
 * Montserrat, so at an equal `font-size` it reads visibly smaller. Multiply
 * accent text by this factor when setting it inline inside surrounding copy.
 * Calibrated against Libre Baskerville.
 */
export const FONT_ACCENT_SCALE = 1.3;

/**
 * Two-tier breakpoint system: anything below `desktop` is mobile.
 *
 * 1280px is the threshold the app's max-content (1200px) + sidebar (240px) +
 * page padding (32px) needs to fit comfortably. The website has no sidebar, but
 * shares the value so "desktop" means the same width on both surfaces.
 */
export const BREAKPOINTS_PX = {
  desktop: 1280,
} as const;
